import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../../components/modal';
import styles from './TnsPaymentModal.module.scss';

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="44" height="44">
      <path d="M5 12.5l4.5 4.5L19 7" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function XCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="44" height="44">
      <path d="M6 6l12 12M6 18L18 6" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CancelledIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="44" height="44">
      <path
        d="M18.364 5.636L5.636 18.364M12 3a9 9 0 100 18A9 9 0 0012 3z"
        stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" width="44" height="44">
      <circle cx="22" cy="22" r="18" fill="none" stroke="#bfdbfe" strokeWidth="4" />
      <path
        d="M22 4a18 18 0 0 1 18 18"
        fill="none"
        stroke="#2563eb"
        strokeWidth="4"
        strokeLinecap="round"
        className={styles.spinnerArc}
      />
    </svg>
  );
}

const STATUS_CONFIG = {
  Success: {
    iconBg: '#dcfce7',
    Icon: CheckCircleIcon,
    paymentTitle: 'Payment Successful!',
    titleColor: '#15803d',
    paymentSubtitle: 'Thank you for your payment.',
    infoBg: '#eff6ff',
    infoBorder: '#bfdbfe',
    infoColor: '#1d4ed8',
    btnBg: '#22c55e',
    btnHover: '#16a34a',
    btnLabel: 'Continue'
  },
  Failed: {
    iconBg: '#fee2e2',
    Icon: XCircleIcon,
    paymentTitle: 'Payment Failed',
    titleColor: '#dc2626',
    paymentSubtitle: 'We were unable to process your payment.',
    infoBg: '#fff1f2',
    infoBorder: '#fecdd3',
    infoColor: '#be123c',
    btnBg: '#ef4444',
    btnHover: '#dc2626',
    btnLabel: 'Try Again'
  },
  Cancelled: {
    iconBg: '#fef3c7',
    Icon: CancelledIcon,
    paymentTitle: 'Payment Cancelled',
    titleColor: '#b45309',
    paymentSubtitle: 'Your payment was cancelled.',
    infoBg: '#fffbeb',
    infoBorder: '#fde68a',
    infoColor: '#92400e',
    btnBg: '#f59e0b',
    btnHover: '#d97706',
    btnLabel: 'Go Back'
  },
  Processing: {
    iconBg: '#dbeafe',
    Icon: SpinnerIcon,
    paymentTitle: 'Payment Processing',
    titleColor: '#1d4ed8',
    paymentSubtitle: 'Please wait while we process your payment.',
    infoBg: '#eff6ff',
    infoBorder: '#bfdbfe',
    infoColor: '#1e40af',
    btnBg: '#3b82f6',
    btnHover: '#2563eb',
    btnLabel: 'Close'
  }
};

function TnsPaymentModal({
  tnsPaymentUrl,
  tnsPaymentModal,
  onClosePaymentModal,
  tnsPaymentMessages,
  tnsPaymentResponse
}) {
  const status = tnsPaymentResponse?.status;
  const config = STATUS_CONFIG[status];

  const cssVars = config
    ? {
      '--icon-bg': config.iconBg,
      '--paymentTitle-color': config.titleColor,
      '--info-bg': config.infoBg,
      '--info-border': config.infoBorder,
      '--info-color': config.infoColor,
      '--btn-bg': config.btnBg,
      '--btn-hover': config.btnHover
    }
    : {
      '--btn-bg': '#6b7280',
      '--btn-hover': '#4b5563'
    };

  return (
    <div>
      {tnsPaymentMessages && (
        <Modal
          showIn={tnsPaymentModal}
          onClose={onClosePaymentModal || (() => {})}
          width="500px"
          minHeight="auto"
          disableOutsideClick
          contentStyles={{ padding: '48px 40px 40px', textAlign: 'center', borderRadius: '16px' }}
        >
          <div style={cssVars}>
            {config && (
              <>
                <div className={styles.iconCircle}>
                  <config.Icon />
                </div>

                <h2 className={styles.paymentTitle}>
                  {config.paymentTitle}
                </h2>

                <p className={styles.paymentSubtitle}>
                  {config.paymentSubtitle}
                </p>

                {tnsPaymentResponse?.message && (
                  <div className={styles.infoBox}>{tnsPaymentResponse.message}</div>
                )}
              </>
            )}

            <hr className={styles.divider} />

            <button
              type="button"
              onClick={onClosePaymentModal}
              className={styles.actionBtn}
            >
              {config ? config.btnLabel : 'Close'}
            </button>
          </div>
        </Modal>
      )}
      {tnsPaymentUrl && (
        <Modal
          showIn={tnsPaymentUrl !== ''}
          onClose={onClosePaymentModal}
          width="900px"
          minHeight="150px"
          disableOutsideClick
        >
          <iframe
            title="Tns-Payment-Status"
            src={tnsPaymentUrl}
            className={styles.iframeEl}
          />
        </Modal>
      )}
    </div>
  );
}

TnsPaymentModal.propTypes = {
  tnsPaymentUrl: PropTypes.string,
  tnsPaymentModal: PropTypes.bool,
  onClosePaymentModal: PropTypes.func.isRequired,
  tnsPaymentMessages: PropTypes.bool,
  tnsPaymentResponse: PropTypes.shape({
    status: PropTypes.oneOf(['Success', 'Failed', 'Cancelled', 'Processing']),
    message: PropTypes.string
  })

};

TnsPaymentModal.defaultProps = {
  tnsPaymentUrl: '',
  tnsPaymentModal: false,
  tnsPaymentMessages: false,
  tnsPaymentResponse: {}
};

export default TnsPaymentModal;
